/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns に並ぶ cell 関数は React component ではなくテーブルセルの render
 * 関数で、独立した component subtree を持たないため lint 警告の前提 (subtree 破棄) に該当しない。
 */
import { useState } from 'react'

import type { ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type DemandBreakdownModeRow,
  DemandBreakdownStatus,
} from '@ebalance/proto/bffuser/v1/demand_breakdown_mode_model_pb'
import type { ListDemandBreakdownModesResponse } from '@ebalance/proto/bffuser/v1/demand_breakdown_mode_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Alert, HStack, Text, VStack } from '@enechain/ecloud-designsystem'

import { Badge } from '~/components/common/Badge'
import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  type EnumBadgeSpec,
  EnumStatusBadge,
} from '~/components/common/EnumStatusBadge'
import {
  AuditCell,
  DeleteIconButton,
  EditIconButton,
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { BreakdownModeEditModal } from '~/pages/DemandSettings/components/BreakdownModeEditModal'
import { DeleteBreakdownModeDialog } from '~/pages/DemandSettings/components/DeleteBreakdownModeDialog'
import {
  breakdownRetailerLabel,
  hasBreakdownSetting,
  rowsInArea,
} from '~/pages/DemandSettings/models/breakdownModeRows'

/** 行の status (backend の判定) → モードの表示 (AC-1 / AC-5 / Decisions #12)。 */
const MODE_BADGE: Record<DemandBreakdownStatus, EnumBadgeSpec> = {
  [DemandBreakdownStatus.UNSPECIFIED]: {
    status: 'neutral',
    labelKey: 'page:demandSettings.breakdownMode.modeDefault',
  },
  [DemandBreakdownStatus.DEFAULT]: {
    status: 'neutral',
    labelKey: 'page:demandSettings.breakdownMode.modeDefault',
  },
  [DemandBreakdownStatus.DIRECT]: {
    status: 'neutral',
    labelKey: 'page:demandSettings.breakdownMode.mode.direct',
  },
  [DemandBreakdownStatus.BY_GROUP]: {
    status: 'info',
    labelKey: 'page:demandSettings.breakdownMode.mode.byGroup',
  },
  [DemandBreakdownStatus.BY_GROUP_NO_GROUPS]: {
    status: 'info',
    labelKey: 'page:demandSettings.breakdownMode.mode.byGroup',
  },
}

type Dialog =
  | { kind: 'edit'; row: DemandBreakdownModeRow }
  | { kind: 'delete'; row: DemandBreakdownModeRow }

/**
 * 需要想定設定画面の「想定モード」タブ。選んだエリアの小売ごとの想定モードを一覧し、設定・編集・
 * 削除する。エリアの切り替えと一覧の取得・公開判定は画面側 (全タブ共通)。
 *
 * 今日の需要想定の対象から外れた小売に残った設定 (`inScope=false`) は、削除だけできる (AC-8)。
 */
export const BreakdownModeTab = ({
  area,
  board,
  isAreaLoading,
}: {
  area: AreaCode | undefined
  board: UseQueryResult<ListDemandBreakdownModesResponse, ConnectError>
  isAreaLoading: boolean
}): React.ReactElement => {
  const { t } = useTranslation()
  const [dialog, setDialog] = useState<Dialog | undefined>(undefined)
  const rows = rowsInArea(board.data?.rows ?? [], area)

  const columns: Array<MasterDataTableColumn<DemandBreakdownModeRow>> = [
    {
      key: 'retailer',
      header: t('page:demandSettings.breakdownMode.column.retailer'),
      cell: (row) => (
        <HStack gap={2}>
          <span>{breakdownRetailerLabel(row)}</span>
          {!row.inScope && (
            <ButtonTooltip
              content={t('page:demandSettings.breakdownMode.outOfScopeHint')}
              focusable
            >
              <Badge status="neutral">
                {t('page:demandSettings.breakdownMode.outOfScope')}
              </Badge>
            </ButtonTooltip>
          )}
        </HStack>
      ),
    },
    {
      key: 'mode',
      header: t('page:demandSettings.breakdownMode.column.mode'),
      cell: (row) => (
        <HStack gap={2}>
          <EnumStatusBadge map={MODE_BADGE} value={row.status} />
          {row.status === DemandBreakdownStatus.BY_GROUP_NO_GROUPS && (
            <ButtonTooltip
              content={t('page:demandSettings.breakdownMode.groupMissingHint')}
              focusable
            >
              <Badge status="warning">
                {t('page:demandSettings.breakdownMode.groupMissing')}
              </Badge>
            </ButtonTooltip>
          )}
        </HStack>
      ),
    },
    {
      key: 'note',
      header: t('page:demandSettings.breakdownMode.column.note'),
      cell: (row) => row.note ?? '',
    },
    {
      key: 'updatedAt',
      header: t('page:demandSettings.breakdownMode.column.updatedAt'),
      nowrap: true,
      cell: (row) => (
        <AuditCell updatedAt={row.updatedAt} updatedBy={row.updatedBy} />
      ),
    },
  ]

  return (
    <VStack alignItems="stretch" flex="1" gap={4} minHeight="0">
      {board.isError && (
        <Alert
          status="error"
          title={t('page:demandSettings.breakdownMode.loadError')}
        />
      )}
      <Text
        as="p"
        color="Text.highEmphasis"
        fontSize="md"
        style={{ margin: 0, lineHeight: 1.6 }}
      >
        {t('page:demandSettings.breakdownMode.description')}
      </Text>
      <MasterDataTable
        actionColWidth="96px"
        columns={columns}
        emptyLabel={t('page:demandSettings.breakdownMode.empty')}
        getRowKey={(row) => `${String(row.areaCode)}-${row.retailerCode}`}
        isLoading={board.isLoading || isAreaLoading}
        renderRowActions={(row) => (
          <>
            {row.inScope && (
              <EditIconButton
                ariaLabel={
                  hasBreakdownSetting(row)
                    ? t('common:action.edit')
                    : t('page:demandSettings.breakdownMode.action.configure')
                }
                onClick={() => setDialog({ kind: 'edit', row })}
              />
            )}
            {hasBreakdownSetting(row) && (
              <DeleteIconButton
                onClick={() => setDialog({ kind: 'delete', row })}
              />
            )}
          </>
        )}
        rows={rows}
      />
      {dialog?.kind === 'edit' && (
        <BreakdownModeEditModal
          row={dialog.row}
          onClose={() => setDialog(undefined)}
        />
      )}
      {dialog?.kind === 'delete' && (
        <DeleteBreakdownModeDialog
          row={dialog.row}
          onClose={() => setDialog(undefined)}
        />
      )}
    </VStack>
  )
}
